import React from 'react'
import { Link } from 'react-router-dom'

import {
    Card,
    CardContent,
    Typography,
    Button,
    IconButton,
    Box,
} from '@material-ui/core'
import {
    MoreVert as MoreVertical,
    ArrowForward as ArrowRight,
    Description as DocumentIcon,
} from '@material-ui/icons'
import { makeStyles, Theme } from '@material-ui/core/styles'

import { Routes } from '../../../navigation/config/routes'
import { getLeaseAuditStep } from '../services'
import { LeaseAuditDetail } from '../types'

interface LeaseAuditCardProps {
    audit: LeaseAuditDetail
    onMenuOpen: (event: React.MouseEvent<HTMLElement>, auditId: number) => void
    superUser: boolean
}

const useStyles = makeStyles((theme: Theme) => ({
    card: {
        position: 'relative',
        overflow: 'hidden',
        border: 'none',
        backgroundColor: '#ffffff',
        boxShadow:
            '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        transition: 'all 0.3s ease',
        '&:hover': {
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        },
    },
    statusIndicator: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: 4,
    },
    cardHeader: {
        padding: '24px 24px 16px 24px',
    },
    cardIcon: {
        padding: '12px',
        borderRadius: '16px',
        background:
            'linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(59, 130, 246, 0.05))',
    },
    cardTitle: {
        fontWeight: 700,
        color: '#1f2937',
        fontSize: '18px',
        marginBottom: '4px',
    },
    cardSubtitle: {
        color: '#6b7280',
        fontSize: '14px',
        marginBottom: '4px',
    },
    statusBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        padding: '4px 12px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: 600,
    },
    statsContainer: {
        padding: theme.spacing(2),
        borderRadius: theme.spacing(1.5),
        background: 'linear-gradient(135deg, #f8fafc, #f1f5f9)',
    },
    statsGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: theme.spacing(2),
        textAlign: 'center',
    },
    viewButton: {
        background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
        color: 'white',
        borderRadius: '8px',
        textTransform: 'none',
        '&:hover': {
            boxShadow:
                '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
        },
    },
    menuButton: {
        width: 32,
        height: 32,
    },
}))

export const LeaseAuditCard: React.FC<LeaseAuditCardProps> = ({
    audit,
    onMenuOpen,
    superUser,
}) => {
    const classes = useStyles()

    const pagesPerDoc =
        audit.document_count > 0
            ? (audit.page_count / audit.document_count).toFixed(2)
            : 'N/A'

    const createdDate = new Date(audit.created_date).toLocaleDateString()

    return (
        <Card className={classes.card}>
            {/* Status Indicator */}
            <Box
                className={classes.statusIndicator}
                style={{
                    backgroundColor: '#10b981',
                }}
            />

            {/* Card Header */}
            <Box className={classes.cardHeader}>
                <Box
                    display="flex"
                    alignItems="flex-start"
                    justifyContent="space-between"
                >
                    <Box
                        display="flex"
                        alignItems="flex-start"
                        style={{ gap: 16 }}
                        flex={1}
                    >
                        <Box className={classes.cardIcon}>
                            <DocumentIcon
                                style={{
                                    height: 24,
                                    width: 24,
                                    color: '#3b82f6',
                                }}
                            />
                        </Box>
                        <Box flex={1} minWidth={0}>
                            {/* Name */}
                            <Typography
                                variant="h6"
                                className={classes.cardTitle}
                            >
                                {audit.name}
                            </Typography>

                            {/* Created By */}
                            <Typography
                                variant="body2"
                                className={classes.cardSubtitle}
                            >
                                Created by:{' '}
                                {audit.created_by?.name || 'Unknown'}
                            </Typography>

                            {/* Created Date */}
                            <Typography
                                variant="body2"
                                className={classes.cardSubtitle}
                            >
                                {createdDate}
                            </Typography>

                            {/* Status Badge */}
                            <Box mt={1}>
                                <span className={classes.statusBadge}>
                                    {getLeaseAuditStep(audit.current_step)}
                                </span>
                            </Box>
                        </Box>
                    </Box>

                    {/* More Menu */}
                    {superUser && (
                        <IconButton
                            size="small"
                            onClick={(e: React.MouseEvent<HTMLElement>) =>
                                onMenuOpen(e, audit.id)
                            }
                            className={classes.menuButton}
                        >
                            <MoreVertical
                                style={{
                                    height: 20,
                                    width: 20,
                                }}
                            />
                        </IconButton>
                    )}
                </Box>
            </Box>

            <CardContent style={{ padding: '0 24px 24px 24px' }}>
                {/* Statistics */}
                <Box className={classes.statsContainer}>
                    <Box className={classes.statsGrid}>
                        <Box>
                            <Typography variant="caption" color="textSecondary">
                                Documents
                            </Typography>
                            <Typography
                                variant="h6"
                                style={{ fontWeight: 'bold' }}
                            >
                                {audit.document_count.toLocaleString()}
                            </Typography>
                        </Box>
                        <Box
                            style={{
                                borderLeft: '1px solid #e0e0e0',
                                borderRight: '1px solid #e0e0e0',
                                padding: '0 8px',
                            }}
                        >
                            <Typography variant="caption" color="textSecondary">
                                Pages
                            </Typography>
                            <Typography
                                variant="h6"
                                color="primary"
                                style={{ fontWeight: 'bold' }}
                            >
                                {audit.page_count?.toLocaleString() ?? 'N/A'}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography variant="caption" color="textSecondary">
                                Pages/Doc
                            </Typography>
                            <Typography
                                variant="h6"
                                color="secondary"
                                style={{ fontWeight: 'bold' }}
                            >
                                {pagesPerDoc}
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                {/* Data Source Tag */}
                {/* <Box mt={2} mb={2}>
                    <Typography
                        variant="caption"
                        style={{
                            backgroundColor: '#f1f5f9',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            color: '#64748b',
                        }}
                    >
                        {audit.data_source === 'INTEGRATED'
                            ? 'Integrated'
                            : 'Uploaded'}
                    </Typography>
                </Box> */}

                {/* View Button */}
                <Link
                    to={Routes.manage.lease.auditDetail.replace(
                        ':id',
                        audit.id.toString(),
                    )}
                    style={{ textDecoration: 'none' }}
                >
                    <Button
                        variant="contained"
                        fullWidth
                        className={classes.viewButton}
                        endIcon={<ArrowRight />}
                    >
                        View Audit
                    </Button>
                </Link>
            </CardContent>
        </Card>
    )
}
